import * as React from 'react'
import { gql } from '@apollo/client'

import { useCodeValidationLazyQuery, useGetInitialDataLazyQuery } from 'types/graphql'

import { useRouter } from 'next/router'
import { OrganizationContext } from 'v2/context/organization'

import { usePotentialUser } from '../v2/context/potentialUser'

export enum LogoutReason {
    General = 'general',
    Inactivity = 'inactivity',
}

interface AuthContextType {
    loading: boolean
}

const InitialValue = {
    loading: true,
}

const AuthContext = React.createContext<AuthContextType>(InitialValue)

interface Props {
    children: React.ReactNode
    loadedTranslations: () => void
}

export const TRANSFER_METHODS_FRAGMENT = gql`
    fragment TransferMethods on UserType {
        transferMethods {
            edges {
                node {
                    id
                    type
                    halfLiner
                    oneLiner
                    isCurrentMethod
                    address1
                    address2
                    city
                    zipCode
                    statusCode
                    deleted
                }
            }
        }
    }
`

export const UPDATE_USER = gql`
    mutation UpdateDetails(
        $firstName: String
        $lastName: String
        $phoneNumber: String
        $dob: String
        $addressLine1: String
        $addressLine2: String
        $city: String
        $state: String
        $postalCode: String
    ) {
        updateDetails(
            firstName: $firstName
            lastName: $lastName
            phoneNumber: $phoneNumber
            dob: $dob
            addressLine1: $addressLine1
            addressLine2: $addressLine2
            city: $city
            state: $state
            postalCode: $postalCode
        ) {
            success
        }
    }
`

export const LOGOUT = gql`
    mutation Logout {
        logout {
            ok
        }
    }
`

export const MAKEPAYMENTS = gql`
    mutation MakePayments($scheduledPaymentIds: [ID]!) {
        makepayments(scheduledPaymentIds: $scheduledPaymentIds) {
            ok
        }
    }
`

const GET_ME = gql`
    ${TRANSFER_METHODS_FRAGMENT}
    query Me {
        me {
            id
            firstName
            lastName
            usioToken
            hyperwalletToken
            phoneNumber
            email
            createdAt
            pk
            isSuperuser
            organization {
                id
                name
            }
            dob
            smsNotificationsEnabled
            emailNotificationsEnabled
            optedForWhatsapp
            paymentAmount
            language
            paymentFrequencyType
            ...TransferMethods
            potentialUser {
                addressLine1
                addressLine2
                city
                postalCode
                state
                id
                intercomKey
                program {
                    id
                    urlFragment
                }
                numberOfMonths
                language
            }
        }
    }
`

const GET_SURVEY_AVAILABLE = gql`
    query SurveyAvailable {
        surveyAvailable
    }
`

const GET_USERS = gql`
    query Users {
        users {
            firstName
            lastName
            pk
            phoneNumber
            paymentAmount
            paymentFrequencyType
            language
            startDate
        }
    }
`

const AuthContextProvider: React.FC<Props> = (props: Props) => {
    const router = useRouter()
    const organizationContext = React.useContext(OrganizationContext)
    const { children } = props
    const potentialUserContext = usePotentialUser()

    const [
        fetchPotentialUser,
        { data: dataPotentialUser, loading: codeValidationLoading, called: calledPotentialUser },
    ] = useCodeValidationLazyQuery()

    const fetchPotentialUserIfNecessary = () => {
        const urlSearchParams = new URLSearchParams(window.location.search)
        if (window.location.pathname.includes('sign-up') && urlSearchParams.get('ic')) {
            fetchPotentialUser({ variables: { inviteCode: urlSearchParams.get('ic') } })
        }
        if (window.location.pathname.includes('reset-password')) {
            fetchPotentialUser({
                variables: {
                    resetToken: window.location.pathname.split('/')[window.location.pathname.split('/').length - 1],
                },
            })
        }
        if (window.location.pathname.includes('verify')) {
            fetchPotentialUser({
                variables: {
                    verificationCode: window.location.pathname.split('/')[
                        window.location.pathname.split('/').length - 1
                    ],
                },
            })
        }
    }

    const [getInitialData, initialData] = useGetInitialDataLazyQuery()

    React.useEffect(() => {
        fetchPotentialUserIfNecessary()
        if (!codeValidationLoading) {
            getInitialData()
        }
    }, [])

    React.useEffect(() => {
        if (calledPotentialUser) {
            if (dataPotentialUser) {
                potentialUserContext.setPotentialUser(dataPotentialUser?.codeValidation?.potentialUser)
                potentialUserContext.setAlreadySignedUp(dataPotentialUser?.codeValidation?.alreadySignedUp)
                window.POTENTIAL_USER = dataPotentialUser?.codeValidation?.potentialUser
            }
        }

        if (!codeValidationLoading && !initialData.called) {
            getInitialData()
        }
    }, [dataPotentialUser])

    const organizationConfigs = React.useMemo(() => {
        return initialData?.data?.organizationConfigs
    }, [initialData?.data])

    const organization = React.useMemo(() => {
        return initialData?.data?.organization
    }, [initialData?.data])

    const program = React.useMemo(() => {
        if (
            (initialData?.data || initialData?.error) &&
            !initialData?.data?.program?.urlFragment &&
            !window.location.href.includes('reset-password')
        ) {
            router.push('/404')
        } else {
            return initialData?.data?.program
        }
    }, [initialData?.data, initialData?.error])

    React.useEffect(() => {
        if (initialData?.data) {
            const jsonBody = initialData?.data?.translations
            if (!jsonBody) {
                return
            }

            const jsonBodyForTranslations = JSON.parse(jsonBody)[0]
            const languages = Object.keys(jsonBodyForTranslations)

            languages.forEach((lang) => {
                const namespaces = Object.keys(jsonBodyForTranslations[lang])
                namespaces.forEach((ns) => {
                    //TODO: This is bad. We'll need to handle these based on some kind of order enum
                    window.LANGUAGES = JSON.parse(jsonBody)[1]
                        .sort((a, b) =>
                            a.code === 'km' ? 1 : b.code === 'km' ? -1 : a.code === 'en' ? -1 : b.code === 'en' ? 1 : 0
                        )
                        .map((language) => {
                            return language.code === 'es'
                                ? {
                                      code: 'es',
                                      text: 'Español',
                                  }
                                : language
                        })
                    window.S18N.addResources(lang, ns, jsonBodyForTranslations[lang][ns])
                })
            })
            props.loadedTranslations()
        }

        if (initialData?.error) {
            props.loadedTranslations()
        }
    }, [initialData?.data, initialData?.error])

    React.useMemo(() => {
        if (initialData?.data?.documentRequirement) {
            window.DOCUMENT_REQ = initialData?.data?.documentRequirement
        }
    }, [initialData?.data])

    if (typeof window !== 'undefined') {
        organizationContext.setOrganization(organization)
        organizationContext.setOrganizationConfigs(organizationConfigs)
        organizationContext.setProgram(program)

        window.ORGANIZATION_CONFIGS = {
            phoneNumber: organizationConfigs?.phoneNumber,
            timezone: organizationConfigs?.timezone,
            replyToEmail: organizationConfigs?.replyToEmail,
            typeformId: organizationConfigs?.typeformId,
            websiteLink: organizationConfigs?.websiteLink,
            instagramLink: organizationConfigs?.instagramLink,
            facebookLink: organizationConfigs?.facebookLink,
            twitterLink: organizationConfigs?.twitterLink,
            typeformSubdomain: organizationConfigs?.typeformSubdomain,
            isTypeformBound: organizationConfigs?.isTypeformBound,
            whatsappPhoneNumber: organizationConfigs?.whatsappPhoneNumber,
        }

        window.ORGANIZATION = {
            version: organization?.version,
            name: organization?.name,
            id: organization?.id,
            webAppUrl: organization?.webAppUrl,
        }

        window.PROGRAM = {
            name: program?.name || '',
            paymentFrequency: program?.paymentFrequency || {},
            paymentAmount: program?.paymentAmount,
            numberOfMonths: program?.numberOfMonths,
            ssiBenefitsWizardEnabled: program?.ssiBenefitsWizardEnabled,
            id: program?.id,
            programDuration: JSON.parse(program?.programDuration || '{}'),
            urlFragment: program?.urlFragment || '',
        }
    }

    return (
        <AuthContext.Provider
            value={{
                loading: codeValidationLoading,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export { AuthContext, AuthContextProvider }
